import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Archive, ArrowLeft, Download, ExternalLink, FileText, Globe, Sparkles, Trash2, Upload } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { AdvertiserOwnerBadge } from "@/components/reassign-advertiser-popover";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { DataTable } from "@/components/data-table";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty";
import { RowActions } from "@/components/ui/row-actions";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { ManifestConfigCard } from "@/components/manifest-config-card";
import { BannersSection } from "@/components/page-detail/banners-section";
import { PagePreview } from "@/components/page-preview";
import {
    deleteVersion,
    downloadVersionUrl,
    getPage,
    listServingDomains,
    listVersions,
    listVersionsPaged,
    retireVersion,
    uploadVersion,
} from "@/api/pages";
import { assignPage, listDomainsPaged } from "@/api/domains";
import { DomainStatusPill } from "@/components/ui/status-pill";
import { Pagination } from "@/components/pagination";
import { apiErrorMessage } from "@/lib/api";
import { cn, formatDate, formatRelativeTime } from "@/lib/utils";
import type { PageVersionDto } from "@/types/api";

export function PageDetailPage(): React.ReactNode {
    const { id } = useParams<{ id: string }>();
    const pageId = Number(id);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const confirm = useConfirm();
    const [dragging, setDragging] = React.useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    // Version being assigned to a domain via the per-row Globe action.
    const [assignVersion, setAssignVersion] = React.useState<PageVersionDto | null>(null);

    const pageQuery = useQuery({
        queryKey: ["page", pageId],
        queryFn: () => getPage(pageId),
        enabled: Number.isFinite(pageId),
    });
    // All-versions fetch (used for preview + "Serving on" lookups — needs the
    // full set, but version counts per page stay small in practice).
    const versionsQuery = useQuery({
        queryKey: ["page", pageId, "versions"],
        queryFn: () => listVersions(pageId),
        enabled: Number.isFinite(pageId),
    });
    // Paginated view for the on-screen Versions table.
    const VERSIONS_PAGE_SIZE = 20;
    const [versionsPageIndex, setVersionsPageIndex] = React.useState(0);
    const versionsPagedQuery = useQuery({
        queryKey: ["page", pageId, "versions-paged", versionsPageIndex],
        queryFn: () =>
            listVersionsPaged(pageId, {
                page: versionsPageIndex,
                size: VERSIONS_PAGE_SIZE,
                sort: "versionNum,desc",
            }),
        enabled: Number.isFinite(pageId),
        placeholderData: (prev) => prev,
    });

    const latestVersion = React.useMemo<PageVersionDto | undefined>(() => {
        const list = versionsQuery.data ?? [];
        if (list.length === 0) return undefined;
        return [...list].sort((a, b) => b.versionNum - a.versionNum)[0];
    }, [versionsQuery.data]);

    // Replaces the prior "load every platform domain + filter client-side"
    // antipattern with a single GET that runs one SQL on the server.
    const servingDomainsQuery = useQuery({
        queryKey: ["page", pageId, "serving-domains"],
        queryFn: () => listServingDomains(pageId),
        enabled: Number.isFinite(pageId),
    });
    const servingDomains = React.useMemo(() => {
        const list = servingDomainsQuery.data ?? [];
        const versions = versionsQuery.data ?? [];
        return list.map((d) => ({
            domain: d,
            version: versions.find((v) => v.id === d.activePageVersionId),
        }));
    }, [servingDomainsQuery.data, versionsQuery.data]);

    // Versions currently live on a domain — retire is blocked for these, and
    // delete would 409 server-side anyway.
    const liveVersionIds = React.useMemo(() => {
        const ids = new Set<number>();
        for (const d of servingDomainsQuery.data ?? []) {
            if (d.activePageVersionId != null) ids.add(d.activePageVersionId);
        }
        return ids;
    }, [servingDomainsQuery.data]);

    const uploadMut = useMutation({
        mutationFn: (file: File) => uploadVersion(pageId, file),
        onSuccess: (version) => {
            toast.success(`Version ${version.versionNum} uploaded`);
            queryClient.invalidateQueries({ queryKey: ["page", pageId, "versions"] });
            queryClient.invalidateQueries({ queryKey: ["page", pageId, "versions-paged"] });
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Upload failed")),
    });

    const retireMut = useMutation({
        mutationFn: (versionId: number) => retireVersion(pageId, versionId),
        onSuccess: (v) => {
            toast.success(`Version ${v.versionNum} retired`);
            queryClient.invalidateQueries({ queryKey: ["page", pageId, "versions"] });
            queryClient.invalidateQueries({ queryKey: ["page", pageId, "versions-paged"] });
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Retire failed")),
    });

    const deleteVersionMut = useMutation({
        mutationFn: (versionId: number) => deleteVersion(pageId, versionId),
        onSuccess: () => {
            toast.success("Version deleted");
            queryClient.invalidateQueries({ queryKey: ["page", pageId, "versions"] });
            queryClient.invalidateQueries({ queryKey: ["page", pageId, "versions-paged"] });
            queryClient.invalidateQueries({ queryKey: ["page", pageId, "serving-domains"] });
        },
        // The backend 409s with a specific reason (live on a domain / captured
        // leads or visits) — surface it verbatim.
        onError: (err) => toast.error(apiErrorMessage(err, "Delete failed")),
    });

    const handleFiles = (files: FileList | null): void => {
        if (!files || files.length === 0) return;
        const file = files[0];
        if (!file.name.toLowerCase().endsWith(".zip")) {
            toast.error("Bundle must be a .zip file");
            return;
        }
        uploadMut.mutate(file);
    };

    const firstLiveDomain = servingDomains.find((s) => s.domain.activePageVersionId != null);

    if (pageQuery.isLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Spinner size="lg" />
            </div>
        );
    }
    if (!pageQuery.data) {
        return (
            <>
                <PageHeader
                    title={`Page #${pageId}`}
                    breadcrumbs={[{ label: "Pages", href: "/pages" }, { label: `#${pageId}` }]}
                />
                <div className="p-6">
                    <EmptyState
                        icon={<FileText className="h-8 w-8" />}
                        title="Page not found"
                        description="It might have been deleted, or the id is wrong."
                        action={
                            <Button asChild>
                                <Link to="/pages"><ArrowLeft className="h-4 w-4" /> Back to pages</Link>
                            </Button>
                        }
                    />
                </div>
            </>
        );
    }

    return (
        <>
            <PageHeader
                title={pageQuery.data?.name ?? (pageQuery.isLoading ? `Page #${pageId}` : "Page")}
                description={
                    <span className="flex flex-wrap items-center gap-2">
                        {pageQuery.data?.description && (
                            <span className="text-muted-foreground">{pageQuery.data.description}</span>
                        )}
                        {pageQuery.data && (
                            <AdvertiserOwnerBadge
                                entityType="page"
                                entityId={pageId}
                                currentAdvertiserId={pageQuery.data.advertiserId ?? null}
                                data-testid="page-advertiser-badge"
                            />
                        )}
                    </span>
                }
                breadcrumbs={[
                    { label: "Pages", href: "/pages" },
                    { label: pageQuery.data?.name ?? `#${pageId}` },
                ]}
                actions={
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" onClick={() => navigate("/pages")}>
                            <ArrowLeft className="h-4 w-4" />
                            Back to pages
                        </Button>
                        {firstLiveDomain && (
                            <Button
                                variant="outline"
                                asChild
                                data-testid="view-live"
                                title={`Open https://${firstLiveDomain.domain.hostname}/ in a new tab`}
                            >
                                <a href={`https://${firstLiveDomain.domain.hostname}/`} target="_blank" rel="noreferrer">
                                    <ExternalLink className="h-4 w-4" />
                                    View live
                                </a>
                            </Button>
                        )}
                        <Button
                            onClick={() => navigate(`/pages/${pageId}/edit`)}
                            data-testid="edit-in-constructor"
                            title="Fork the latest version into a new AI-constructor draft you can iterate on"
                        >
                            <Sparkles className="h-4 w-4" />
                            Edit in Constructor
                        </Button>
                    </div>
                }
            />
            <div className="p-6 space-y-6">
                <UploadBundleSection
                    hasExistingVersions={(versionsQuery.data?.length ?? 0) > 0}
                    isPending={uploadMut.isPending}
                    dragging={dragging}
                    setDragging={setDragging}
                    handleFiles={handleFiles}
                    fileInputRef={fileInputRef}
                />

                {latestVersion && (
                    <PagePreview
                        pageId={pageId}
                        versionId={latestVersion.id}
                        versionNum={latestVersion.versionNum}
                        r2Prefix={latestVersion.r2Prefix}
                        manifest={latestVersion.manifest}
                    />
                )}

                {servingDomains.length > 0 && (
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base">Serving on</CardTitle>
                            <CardDescription>
                                Domains currently assigned to a version of this page. Click a hostname to open the
                                live landing page in a new tab.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-2">
                            {servingDomains.map(({ domain, version }) => (
                                <div
                                    key={domain.id}
                                    className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3"
                                >
                                    <div className="flex items-start gap-3 min-w-0">
                                        <Globe className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <Link
                                                    to={`/domains/${domain.id}`}
                                                    className="truncate font-medium hover:underline"
                                                >
                                                    {domain.hostname}
                                                </Link>
                                                <DomainStatusPill status={domain.status} />
                                            </div>
                                            <div className="mt-0.5 text-xs text-muted-foreground">
                                                {version ? `v${version.versionNum} · ${version.status}` : `v#${domain.activePageVersionId}`}
                                                {domain.gaCode && <> · GA <code className="font-mono">{domain.gaCode}</code></>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            asChild
                                            size="sm"
                                            variant="outline"
                                            data-testid={`open-live-${domain.hostname}`}
                                        >
                                            <a
                                                href={`https://${domain.hostname}/`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Open live
                                                <ExternalLink className="ml-1 h-3 w-3" />
                                            </a>
                                        </Button>
                                        <Button asChild size="sm" variant="ghost">
                                            <Link to={`/domains/${domain.id}`}>Manage</Link>
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </CardContent>
                    </Card>
                )}

                {latestVersion && <ManifestConfigCard pageId={pageId} version={latestVersion} />}

                <BannersSection pageId={pageId} />

                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Versions</CardTitle>
                        <CardDescription>Every upload creates a new version.</CardDescription>
                    </CardHeader>
                    <CardContent className="p-0">
                        {versionsPagedQuery.isLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <Spinner size="lg" />
                            </div>
                        ) : (
                            <DataTable<PageVersionDto>
                                dataTestid="versions-table"
                                className="rounded-none border-0"
                                columns={[
                                    {
                                        key: "version",
                                        header: "Version",
                                        cell: (v) => <span className="font-medium">v{v.versionNum}</span>,
                                    },
                                    {
                                        key: "status",
                                        header: "Status",
                                        cell: (v) => v.status === "RETIRED" ? (
                                            <Badge variant="muted">Retired</Badge>
                                        ) : (
                                            <Badge variant={v.status === "VALIDATED" ? "success" : "muted"}>
                                                {v.status}
                                            </Badge>
                                        ),
                                    },
                                    {
                                        key: "prefix",
                                        header: "R2 prefix",
                                        cell: (v) => (
                                            <code className="font-mono text-xs text-muted-foreground">
                                                {v.r2Prefix}
                                            </code>
                                        ),
                                    },
                                    {
                                        key: "uploaded",
                                        header: "Uploaded",
                                        cell: (v) => (
                                            <span className="text-muted-foreground" title={formatDate(v.uploadedAt)}>
                                                {formatRelativeTime(v.uploadedAt)}
                                            </span>
                                        ),
                                    },
                                    {
                                        key: "actions",
                                        header: "",
                                        className: "text-right",
                                        cell: (v) => {
                                            const live = liveVersionIds.has(v.id);
                                            return (
                                                <RowActions>
                                                    <Button
                                                        asChild
                                                        size="sm"
                                                        variant="ghost"
                                                        title="Download bundle (.zip)"
                                                        data-testid={`download-version-${v.id}`}
                                                    >
                                                        <a
                                                            href={downloadVersionUrl(pageId, v.id)}
                                                            download
                                                            aria-label={`Download v${v.versionNum} bundle`}
                                                        >
                                                            <Download className="h-3.5 w-3.5" />
                                                        </a>
                                                    </Button>
                                                    {v.status !== "RETIRED" && (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            title="Assign to a domain"
                                                            aria-label={`Assign v${v.versionNum} to a domain`}
                                                            onClick={() => setAssignVersion(v)}
                                                            data-testid={`assign-version-${v.id}`}
                                                        >
                                                            <Globe className="h-3.5 w-3.5" />
                                                        </Button>
                                                    )}
                                                    {v.status !== "RETIRED" && (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            disabled={live || retireMut.isPending}
                                                            title={live
                                                                ? "Live on a domain — reassign that domain first"
                                                                : "Retire — keeps history, removes it from the assign picker"}
                                                            aria-label={`Retire v${v.versionNum}`}
                                                            onClick={() => confirm.show({
                                                                title: `Retire v${v.versionNum}?`,
                                                                description: "The version stays in history but drops out of the assign picker, so it can't be rolled onto a domain by mistake.",
                                                                confirmLabel: "Retire",
                                                                "data-testid": "retire-version-confirm",
                                                                onConfirm: async () => {
                                                                    await new Promise<void>((resolve, reject) => {
                                                                        retireMut.mutate(v.id, { onSuccess: () => resolve(), onError: (e) => reject(e) });
                                                                    });
                                                                },
                                                            })}
                                                            data-testid={`retire-version-${v.id}`}
                                                        >
                                                            <Archive className="h-3.5 w-3.5" />
                                                        </Button>
                                                    )}
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        disabled={deleteVersionMut.isPending}
                                                        title="Delete permanently — refused if live on a domain or if it captured leads/visits"
                                                        aria-label={`Delete v${v.versionNum}`}
                                                        onClick={() => confirm.show({
                                                            title: `Delete v${v.versionNum} permanently?`,
                                                            description: "Deletes this version and its bundle files. This can't be undone.",
                                                            destructive: true,
                                                            confirmLabel: "Delete permanently",
                                                            "data-testid": "delete-version-confirm",
                                                            onConfirm: async () => {
                                                                await new Promise<void>((resolve, reject) => {
                                                                    deleteVersionMut.mutate(v.id, { onSuccess: () => resolve(), onError: (e) => reject(e) });
                                                                });
                                                            },
                                                        })}
                                                        data-testid={`delete-version-${v.id}`}
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                                                    </Button>
                                                </RowActions>
                                            );
                                        },
                                    },
                                ]}
                                rows={versionsPagedQuery.data?.content ?? []}
                                rowKey={(v) => v.id}
                                emptyState={
                                    <EmptyState
                                        title="No versions yet"
                                        description="Upload your first bundle to get started."
                                    />
                                }
                            />
                        )}
                        {versionsPagedQuery.data && (
                            <div className="px-4 pb-2">
                                <Pagination
                                    page={versionsPagedQuery.data}
                                    pageSize={VERSIONS_PAGE_SIZE}
                                    currentPage={versionsPageIndex}
                                    onPage={setVersionsPageIndex}
                                />
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Link to="/domains" className="block text-sm text-muted-foreground hover:underline">
                    → Assign this page to a domain
                </Link>
            </div>
            {confirm.dialog}
            <AssignVersionDialog
                pageId={pageId}
                version={assignVersion}
                onClose={() => setAssignVersion(null)}
            />
        </>
    );
}

/**
 * "Assign this version to a domain" — the page-side mirror of the domain-detail
 * assign card. Lists AVAILABLE (pool) and ASSIGNED (live) domains; picking one
 * calls the same POST /admin/domains/{id}/assign the domain side uses, so an
 * operator can roll a domain to a specific (older) version without leaving the
 * page screen.
 */
function AssignVersionDialog({
    pageId,
    version,
    onClose,
}: {
    pageId: number;
    version: PageVersionDto | null;
    onClose: () => void;
}): React.ReactNode {
    const queryClient = useQueryClient();
    const open = version != null;
    const domainsQuery = useQuery({
        queryKey: ["domains", "assignable"],
        queryFn: async () => {
            const [available, assigned] = await Promise.all([
                listDomainsPaged({ status: "AVAILABLE", size: 500 }),
                listDomainsPaged({ status: "ASSIGNED", size: 500 }),
            ]);
            return [...available.content, ...assigned.content]
                .sort((a, b) => a.hostname.localeCompare(b.hostname));
        },
        enabled: open,
    });
    const assignMut = useMutation({
        mutationFn: (domainId: number) => assignPage(domainId, (version as PageVersionDto).id),
        onSuccess: (d) => {
            toast.success(`v${version?.versionNum} now serving on ${d.hostname}`);
            queryClient.invalidateQueries({ queryKey: ["page", pageId, "serving-domains"] });
            queryClient.invalidateQueries({ queryKey: ["domains"] });
            queryClient.invalidateQueries({ queryKey: ["domain", d.id] });
            onClose();
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Assign failed")),
    });
    const domains = domainsQuery.data ?? [];
    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o && !assignMut.isPending) onClose(); }}>
            <DialogContent data-testid="assign-version-dialog">
                <DialogHeader>
                    <DialogTitle>Assign v{version?.versionNum} to a domain</DialogTitle>
                    <DialogDescription>
                        The domain starts serving this exact version — the edge cache updates within ~30s.
                        Assigning over a live domain replaces whatever it serves now.
                    </DialogDescription>
                </DialogHeader>
                {domainsQuery.isLoading ? (
                    <div className="flex justify-center py-8"><Spinner size="lg" /></div>
                ) : domains.length === 0 ? (
                    <EmptyState
                        icon={<Globe className="h-8 w-8" />}
                        title="No domains ready for assignment"
                        description="Only AVAILABLE or ASSIGNED domains can serve a page. Provision one first."
                        action={
                            <Button asChild variant="outline">
                                <Link to="/domains">Go to domains</Link>
                            </Button>
                        }
                    />
                ) : (
                    <div className="max-h-[320px] space-y-1.5 overflow-y-auto pr-1">
                        {domains.map((d) => {
                            const already = version != null && d.activePageVersionId === version.id;
                            return (
                                <div
                                    key={d.id}
                                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
                                >
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="truncate text-sm font-medium">{d.hostname}</span>
                                            <DomainStatusPill status={d.status} />
                                        </div>
                                        <div className="mt-0.5 text-xs text-muted-foreground">
                                            {d.activePageVersionId != null ? (
                                                <>
                                                    serving {d.activePageName ?? `version #${d.activePageVersionId}`}
                                                    {d.activePageVersionNum != null && ` v${d.activePageVersionNum}`}
                                                </>
                                            ) : (
                                                "not serving anything yet"
                                            )}
                                        </div>
                                    </div>
                                    <Button
                                        size="sm"
                                        variant={already ? "ghost" : "outline"}
                                        disabled={already || assignMut.isPending}
                                        onClick={() => assignMut.mutate(d.id)}
                                        data-testid={`assign-domain-${d.id}`}
                                    >
                                        {assignMut.isPending && assignMut.variables === d.id
                                            ? <Spinner size="sm" />
                                            : already ? "Already serving" : "Assign"}
                                    </Button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

/**
 * Upload bundle UI. Renders as the hero dropzone for new pages; collapses
 * into a small inline "Upload new version" bar once at least one version
 * exists, so the page-detail screen isn't dominated by an empty drop-zone
 * after the first upload.
 */
function UploadBundleSection({
    hasExistingVersions, isPending, dragging, setDragging, handleFiles, fileInputRef,
}: {
    hasExistingVersions: boolean;
    isPending: boolean;
    dragging: boolean;
    setDragging: (d: boolean) => void;
    handleFiles: (files: FileList | null) => void;
    fileInputRef: React.RefObject<HTMLInputElement | null>;
}): React.ReactNode {
    // React 19's RefObject<T|null> isn't directly assignable to the JSX `ref`
    // prop's RefObject<T>. The runtime semantics are identical; this cast just
    // satisfies the type checker.
    const inputRef = fileInputRef as React.RefObject<HTMLInputElement>;
    const [expanded, setExpanded] = React.useState(!hasExistingVersions);
    React.useEffect(() => { if (!hasExistingVersions) setExpanded(true); }, [hasExistingVersions]);
    if (hasExistingVersions && !expanded) {
        return (
            <div className="flex items-center gap-3 rounded-md border border-border bg-card px-4 py-2 text-sm">
                <Upload className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Upload a new version (zip with landing.json + entry HTML)</span>
                <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setExpanded(true)} data-testid="expand-upload">
                    Expand
                </Button>
                <input
                    ref={inputRef}
                    id="bundle-input"
                    data-testid="bundle-input"
                    type="file"
                    className="hidden"
                    accept=".zip,application/zip"
                    onChange={(e) => handleFiles(e.target.files)}
                />
                <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={isPending}>
                    {isPending ? <Spinner size="sm" /> : "Choose .zip"}
                </Button>
            </div>
        );
    }
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">{hasExistingVersions ? "Upload new version" : "Upload bundle"}</CardTitle>
                <CardDescription>
                    Drop a zipped bundle here. It must contain{" "}
                    <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">landing.json</code>{" "}
                    at the root and the entry HTML file (default{" "}
                    <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">index.html</code>).
                </CardDescription>
            </CardHeader>
            <CardContent>
                <label
                    htmlFor="bundle-input"
                    onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
                    className={cn(
                        "flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed py-10 text-center transition-colors",
                        dragging ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-muted-foreground/50"
                    )}
                    data-testid="bundle-dropzone"
                >
                    {isPending ? (
                        <>
                            <Spinner size="lg" />
                            <p className="mt-3 text-sm text-muted-foreground">Uploading and validating…</p>
                        </>
                    ) : (
                        <>
                            <Upload className="h-8 w-8 text-muted-foreground" />
                            <p className="mt-3 text-sm font-medium">Drop the zip here, or click to browse</p>
                            <p className="mt-1 text-xs text-muted-foreground">Max 50&nbsp;MB · bundles are validated against the manifest contract</p>
                        </>
                    )}
                    <input
                        ref={inputRef}
                        id="bundle-input"
                        data-testid="bundle-input"
                        type="file"
                        className="hidden"
                        accept=".zip,application/zip"
                        onChange={(e) => handleFiles(e.target.files)}
                        disabled={isPending}
                    />
                </label>
                {hasExistingVersions && (
                    <button
                        type="button"
                        className="mt-2 text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => setExpanded(false)}
                    >
                        Collapse
                    </button>
                )}
            </CardContent>
        </Card>
    );
}
